import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import dayjs, { Dayjs } from 'dayjs';
import { Person, RealEstate, TenantUnitDetail } from '@eadsysteme/ewsrealestateclient-v3';
import { RoleSetting } from '@eadsysteme/energyefficiencyclient-v1';
import { Role, RoleModel, RoleType, UiRoleType } from '../../../../services/RoleModel';
import { Permission } from '../../../../services/data/permission';
import { DateUtil } from '../../../../util/DateUtil';
import { PersonDisplayUtil } from '../../../../util/PersonDisplayUtil';
import { RolesDisplayService } from '../roles-display.service';
import { TimePeriod } from '../time-period.service';
import { UviReceptionRights } from '../uvi-reception-rights.service';
import { PersonEditorDialogData, PersonEditorPersonOption } from './person-editor-dialog.types';
import { StrandContextFactory } from './strand-context.factory';
import RoleTypeEnum = RoleSetting.RoleTypeEnum;

/** Zustand des Personen-Tabs, aus dem der Editor-Vertrag gebaut wird. */
export interface EditorTabContext {
	realEstate: RealEstate | null;
	tenantUnit: TenantUnitDetail | null;
	realEstateArea: number;
	realEstateId: string;
	tenantUnitId: string;
	roleModels: RoleModel[];
	availablePeriodsByRoleType: Map<RoleTypeEnum, TimePeriod[]>;
	availableRoleTypes: RoleType[];
	permissions: { [roleId: string]: Permission[] };
	uviReceptionRights: UviReceptionRights;
	isPropertyManagement: boolean;
}

/** Editor-Daten ohne den Aufrufkontext — den ergänzt der Aufrufer je Button. */
export type PersonEditorBaseData = Omit<PersonEditorDialogData, 'entryContext'>;

/** Baut den {@link PersonEditorDialogData}-Vertrag aus dem aktuellen Tab-Zustand (RoleModels + Kontext). */
@Injectable({ providedIn: 'root' })
export class PersonEditorDataFactory {
	private readonly strandContextFactory = inject(StrandContextFactory);
	private readonly personDisplayUtil = inject(PersonDisplayUtil);
	private readonly translocoService = inject(TranslocoService);
	private readonly rolesDisplay = inject(RolesDisplayService);
	private readonly dateUtil = inject(DateUtil);

	/** Daten für das Anlegen (neue Person/Rolle/Leerstand); `preselectedRoleTypes` für den Rollen-Kopier-Flow. */
	buildAddData(context: EditorTabContext, preselectedRoleTypes: RoleType[] = [], excludedPerson?: Person): PersonEditorBaseData {
		const preselectedRoles: Role[] = preselectedRoleTypes
			.filter((roleType) => roleType !== UiRoleType.VACANCY)
			.filter((roleType, index, all) => all.indexOf(roleType) === index)
			.map((roleType) => ({ roleType }));

		return {
			...this.buildCommonData(context, excludedPerson),
			allAvailablePeriodsByRoleType: new Map(context.availablePeriodsByRoleType),
			person: undefined,
			roles: preselectedRoles.length ? preselectedRoles : undefined,
		};
	}

	/** Daten für das Bearbeiten einer bestehenden Person samt ihrer Rollen. */
	buildEditData(context: EditorTabContext, roleModel: RoleModel): PersonEditorBaseData {
		return {
			...this.buildCommonData(context),
			person: roleModel.person,
			roles: this.withUviReception(roleModel.roles),
			strandContexts: this.strandContextFactory.build(context.roleModels, roleModel.person?.id, context.availableRoleTypes),
		};
	}

	/**
	 * Spiegelt `roleSetting.receptionType` ins UI-Feld `uviReception` — ohne das ersetzt der Editor
	 * ein bestehendes PORTAL/NO_EED beim Speichern durch PRINT. Kopierend, weil die Rollen-Objekte am
	 * Personen-Tab hängen, dessen uVI-Spalte weiterhin `role.roleSetting` liest.
	 */
	private withUviReception(roles: Role[] | undefined): Role[] | undefined {
		return roles?.map((role) =>
			role.roleType === UiRoleType.RESIDENT ? { ...role, uviReception: role.uviReception ?? role.roleSetting?.receptionType } : role
		);
	}

	private buildCommonData(context: EditorTabContext, excludedPerson?: Person): PersonEditorBaseData {
		return {
			allAvailablePeriodsByRoleType: context.availablePeriodsByRoleType,
			availableRoleTypes: context.availableRoleTypes,
			person: undefined,
			roles: undefined,
			currentRealEstateId: context.realEstateId,
			currentRealEstateArea: context.realEstateArea,
			currentRealEstateNumber: context.realEstate?.number,
			currentTenantunitId: context.tenantUnitId,
			currentTenantUnitNumber: context.tenantUnit?.number,
			currentTenantUnitSubNumber: context.tenantUnit?.subNumber,
			permissions: context.permissions,
			uviReceptionRights: context.uviReceptionRights,
			isPropertyManagement: context.isPropertyManagement,
			personOptions: this.buildPersonOptions(context.roleModels, excludedPerson),
			// Beim Anlegen hat die Person noch keine Rollen — die Kontextzeilen bleiben leer, bis
			// buildEditData beziehungsweise selectKnownPerson eine konkrete Person kennt.
			strandContexts: this.strandContextFactory.build(context.roleModels, undefined, context.availableRoleTypes),
			realEstateAddress: context.realEstate?.address,
			roleModels: context.roleModels,
			roleIdsByType: this.buildRoleIdsByType(context.roleModels),
			latestRoleIdByStrand: this.buildLatestRoleIdByStrand(context.roleModels),
		};
	}

	/**
	 * Zuletzt beginnende Rolle je Zeitstrang. Nur an ihr verarbeitet das Backend noch Datumsänderungen;
	 * frühere Einträge (etwa ein Leerstand vor einer schon folgenden Rolle) bleiben deshalb gesperrt.
	 */
	private buildLatestRoleIdByStrand(roleModels: RoleModel[]): Map<RoleType, string> {
		const latest = new Map<RoleType, { id: string; validFrom: number }>();
		for (const roleModel of roleModels ?? []) {
			for (const role of roleModel.roles ?? []) {
				if (!role.id || !role.roleType) {
					continue;
				}
				const strandType = StrandContextFactory.strandTypeOf(role.roleType);
				const validFrom = role.validFrom ? dayjs(role.validFrom).valueOf() : DateUtil.minDate.valueOf();
				const current = latest.get(strandType);
				if (!current || validFrom > current.validFrom) {
					latest.set(strandType, { id: role.id, validFrom });
				}
			}
		}
		return new Map([...latest].map(([strandType, entry]) => [strandType, entry.id]));
	}

	private buildRoleIdsByType(roleModels: RoleModel[]): Map<RoleType, string[]> {
		const idsByType = new Map<RoleType, string[]>();
		for (const roleModel of roleModels ?? []) {
			for (const role of roleModel.roles ?? []) {
				if (!role.roleType) {
					continue;
				}
				const ids = idsByType.get(role.roleType) ?? [];
				ids.push(role.id ?? '');
				idsByType.set(role.roleType, ids);
			}
		}
		return idsByType;
	}

	private buildPersonOptions(roleModels: RoleModel[], excludedPerson?: Person): PersonEditorPersonOption[] {
		return (roleModels ?? [])
			.filter((roleModel) => !roleModel.isVacancy && !!roleModel.person)
			.filter((roleModel) => !this.isSourcePerson(roleModel.person, excludedPerson))
			.map((roleModel) => {
				const person = roleModel.person;
				const label = this.getPersonLabel(person);
				const rolesLabel = Array.from(new Set((roleModel.roles ?? []).map((role) => this.rolesDisplay.roleTypeLabel(role.roleType)))).join(', ');
				const datesLabel = this.getLatestRoleDateLabel(roleModel.roles ?? []);

				return {
					id: person?.id ?? `${label}-${rolesLabel}-${datesLabel}`,
					label,
					rolesLabel,
					datesLabel,
					person,
					// `selectKnownPerson()` lädt die Rollen aus der Option neu — sonst ist der Wert wieder weg.
					roles: this.withUviReception(roleModel.roles) ?? [],
				};
			})
			.filter((option) => !!option.id);
	}

	private getLatestRoleDateLabel(roles: Role[]): string {
		const latestFrom = roles
			.map((role) => (role.validFrom ? dayjs(role.validFrom) : null))
			.filter((value): value is Dayjs => !!value)
			.sort((a, b) => b.valueOf() - a.valueOf())[0];
		return latestFrom ? this.dateUtil.checkDateValid(latestFrom) : '-';
	}

	private getPersonLabel(person?: Person): string {
		if (!person) {
			return this.translocoService.translate('user.unknown');
		}

		const name = this.personDisplayUtil.getLongName(person);
		if (name !== '-') {
			return name;
		}

		return person.email?.trim() || this.translocoService.translate('user.unknown');
	}

	/** Erkennt, ob `candidate` dieselbe Person wie `source` ist (id, E-Mail oder Namensgleichheit). */
	private isSourcePerson(candidate?: Person, source?: Person): boolean {
		if (!candidate || !source) {
			return false;
		}
		if (candidate.id && source.id) {
			return candidate.id === source.id;
		}

		const normalize = (value?: string) => (value ?? '').trim().toLowerCase();
		const candidateEmail = normalize(candidate.email);
		const sourceEmail = normalize(source.email);
		if (candidateEmail && sourceEmail && candidateEmail === sourceEmail) {
			return true;
		}

		const candidateFullName = normalize(candidate.fullName || `${candidate.givenName ?? ''} ${candidate.familyName ?? ''}`);
		const sourceFullName = normalize(source.fullName || `${source.givenName ?? ''} ${source.familyName ?? ''}`);
		return !!candidateFullName && !!sourceFullName && candidateFullName === sourceFullName;
	}
}
